import type { HTMLAttributes } from "react";
import { cn } from "./lib/cn";
export type LoaderProps = HTMLAttributes<HTMLDivElement> & {
  size?: number;
  /**
   * Accessible name for the spinner.
   *
   * The SVG title is the only text assistive technology can reach here, so a host that shows this
   * to users must pass a localized string; an `aria-label` on the wrapper would be ignored because
   * a plain `div` has no role to name.
   */
  label?: string;
};
/**
 * Animated loading indicator adapted from the current AI Elements source.
 *
 * The spin runs on the fixed-size SVG, not the wrapper: a flex column stretches the wrapper to
 * full width, and rotating a wide box sweeps a huge bounding box through the scroll container's
 * scrollable overflow, making scrollHeight oscillate and stick-to-bottom scrolling bounce.
 * `overflow-hidden` clips the rotated corners, which are always empty pixels.
 */
export const Loader = ({ className, size = 16, label = "Loading", ...props }: LoaderProps) => (
  <div
    className={cn("inline-flex items-center justify-center overflow-hidden", className)}
    {...props}
  >
    <svg
      className="animate-spin"
      height={size}
      strokeLinejoin="round"
      style={{ color: "currentcolor" }}
      viewBox="0 0 16 16"
      width={size}
    >
      <title>{label}</title>
      <g clipPath="url(#ml-ai-loader-clip)">
        <path d="M8 0V4" stroke="currentColor" strokeWidth="1.5" />
        <path d="M8 16V12" opacity="0.5" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M3.29773 1.52783L5.64887 4.7639"
          opacity="0.9"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path
          d="M12.7023 1.52783L10.3511 4.7639"
          opacity="0.1"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path
          d="M12.7023 14.472L10.3511 11.236"
          opacity="0.4"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path
          d="M3.29773 14.472L5.64887 11.236"
          opacity="0.6"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path
          d="M15.6085 5.52783L11.8043 6.7639"
          opacity="0.2"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path
          d="M0.391602 10.472L4.19583 9.23598"
          opacity="0.7"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path
          d="M15.6085 10.4722L11.8043 9.2361"
          opacity="0.3"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path
          d="M0.391602 5.52783L4.19583 6.7639"
          opacity="0.8"
          stroke="currentColor"
          strokeWidth="1.5"
        />
      </g>
      <defs>
        <clipPath id="ml-ai-loader-clip">
          <rect fill="white" height="16" width="16" />
        </clipPath>
      </defs>
    </svg>
  </div>
);
